import { useState, useEffect } from 'react';
import { Users, UserPlus, Loader2, Shield, ShieldOff, AlertCircle, Mail, Trash2, CheckCircle2 } from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';

interface OrgUser {
  id: string;
  email: string;
  full_name: string;
  role: 'member' | 'sensor';
  status: string;
  is_member_admin: boolean;
  is_primary_contact: boolean;
  email_verified: boolean;
  approved_at: string | null;
  created_at: string;
}

interface PendingInvitation {
  id: string;
  email: string;
  role: string;
  expires_at: string;
  created_at: string;
}

export default function MemberOrgUserManagement({ memberId }: { memberId: string }) {
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [pending, setPending] = useState<PendingInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Invite form
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'member' | 'sensor'>('member');
  const [inviteAsAdmin, setInviteAsAdmin] = useState(false);
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [memberId]);

  async function loadUsers() {
    setLoading(true);
    setError(null);
    try {
      const r = await authenticatedFetch(`/api/admin/members/${memberId}/users`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Kunne ikke laste brukere');
      setUsers(d.users || []);
      setPending(d.pending_invitations || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setInviting(true);
    setError(null);
    setSuccess(null);
    try {
      const r = await authenticatedFetch(`/api/admin/members/${memberId}/users/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: inviteEmail,
          role: inviteRole,
          is_member_admin: inviteRole === 'member' ? inviteAsAdmin : false
        })
      });
      const d = await r.json();
      if (!r.ok) {
        // Spesielt: hvis domene ikke godkjent, vis godkjente domener
        if (d.approved_domains?.length > 0) {
          setError(`${d.error}\nGodkjente domener: ${d.approved_domains.join(', ')}`);
        } else {
          setError(d.error || 'Kunne ikke invitere');
        }
        return;
      }
      setSuccess(`Invitasjon sendt til ${inviteEmail}${inviteAsAdmin && inviteRole === 'member' ? ' (med administrator-rettigheter)' : ''}`);
      setInviteEmail('');
      setInviteAsAdmin(false);
      setShowInvite(false);
      await loadUsers();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setInviting(false);
    }
  }

  async function changeStatus(userId: string, newStatus: 'active' | 'inactive') {
    if (!confirm(`Sett bruker som ${newStatus === 'active' ? 'aktiv' : 'inaktiv'}?`)) return;
    setError(null);
    try {
      const r = await authenticatedFetch(`/api/admin/users/${userId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      await loadUsers();
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function changeRole(userId: string, newRole: 'member' | 'sensor') {
    setError(null);
    try {
      const r = await authenticatedFetch(`/api/admin/users/${userId}/role`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      await loadUsers();
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function togglePromote(user: OrgUser) {
    const action = user.is_member_admin ? 'demote-member-admin' : 'promote-member-admin';
    if (!confirm(user.is_member_admin
      ? `Fjern administrator-rolle fra ${user.email}?`
      : `Gi ${user.email} administrator-rettigheter for organisasjonen?`)) return;

    setError(null);
    try {
      const r = await authenticatedFetch(`/api/admin/users/${user.id}/${action}`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      await loadUsers();
    } catch (e: any) {
      setError(e.message);
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Users size={22} />
            Brukere i organisasjonen
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Administrer brukere som har tilgang til organisasjonen din. Inviter nye, fjern brukere som har sluttet, og utpek flere administratorer.
          </p>
        </div>
        {!showInvite && (
          <button
            onClick={() => setShowInvite(true)}
            className="flex items-center gap-2 bg-kf-primary text-white px-4 py-2 rounded-lg hover:bg-kf-primary-dark transition"
          >
            <UserPlus size={18} />
            Inviter bruker
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800 m-0 whitespace-pre-line">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-800 m-0">{success}</p>
        </div>
      )}

      {/* Invite form */}
      {showInvite && (
        <form onSubmit={handleInvite} className="border border-blue-200 bg-blue-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <Mail size={18} /> Inviter ny bruker
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">E-postadresse</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="navn@dittfirma.no"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              <p className="text-xs text-gray-600 mt-1">
                E-posten må tilhøre et godkjent domene. Sensorer kan ha ekstern e-post.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rolle</label>
              <select
                value={inviteRole}
                onChange={(e) => { setInviteRole(e.target.value as 'member' | 'sensor'); if (e.target.value === 'sensor') setInviteAsAdmin(false); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="member">Medlem</option>
                <option value="sensor">Sensor</option>
              </select>
            </div>
          </div>

          {/* Administrator-checkbox kun for medlem-rolle */}
          {inviteRole === 'member' && (
            <label className="flex items-start gap-2 mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={inviteAsAdmin}
                onChange={(e) => setInviteAsAdmin(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-sm">
                <strong className="text-purple-900">Gi administrator-rettigheter</strong>
                <p className="text-purple-800 m-0 mt-0.5 text-xs">
                  Denne brukeren vil kunne se og administrere andre brukere + foreslå nye e-postdomener for organisasjonen.
                </p>
              </span>
            </label>
          )}

          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              onClick={() => { setShowInvite(false); setInviteEmail(''); setError(null); }}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Avbryt
            </button>
            <button
              type="submit"
              disabled={inviting || !inviteEmail}
              className="px-4 py-2 bg-kf-primary text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
            >
              {inviting && <Loader2 className="w-4 h-4 animate-spin" />}
              Send invitasjon
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      ) : (
        <>
          {/* Aktive brukere */}
          {users.length === 0 ? (
            <div className="text-center py-8 text-gray-500 border border-dashed rounded-lg">
              <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>Ingen brukere registrert ennå.</p>
              <p className="text-sm">Inviter den første brukeren for å komme i gang.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {users.map((u) => (
                <div key={u.id} className="border border-gray-200 rounded-lg p-3 flex items-start justify-between gap-3 bg-white">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <strong className="text-gray-900">{u.full_name || u.email}</strong>
                      <span className="text-sm text-gray-500">{u.email}</span>
                      {u.is_member_admin && (
                        <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full flex items-center gap-1">
                          <Shield size={10} /> Administrator
                        </span>
                      )}
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        u.role === 'sensor' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {u.role === 'sensor' ? 'Sensor' : 'Medlem'}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        u.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {u.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {/* Endre rolle */}
                    {u.role !== 'sensor' && (
                      <button
                        onClick={() => changeRole(u.id, 'sensor')}
                        title="Gjør til sensor"
                        className="p-2 text-gray-500 hover:bg-gray-100 rounded text-xs"
                      >
                        → Sensor
                      </button>
                    )}
                    {u.role === 'sensor' && (
                      <button
                        onClick={() => changeRole(u.id, 'member')}
                        title="Gjør til medlem"
                        className="p-2 text-gray-500 hover:bg-gray-100 rounded text-xs"
                      >
                        → Medlem
                      </button>
                    )}
                    {/* Promote/demote admin */}
                    {u.role === 'member' && (
                      <button
                        onClick={() => togglePromote(u)}
                        title={u.is_member_admin ? 'Fjern administrator' : 'Gjør til administrator'}
                        className="p-2 text-purple-600 hover:bg-purple-50 rounded"
                      >
                        {u.is_member_admin ? <ShieldOff size={16} /> : <Shield size={16} />}
                      </button>
                    )}
                    {/* Aktiver/deaktiver */}
                    {u.status === 'active' ? (
                      <button
                        onClick={() => changeStatus(u.id, 'inactive')}
                        title="Deaktiver"
                        className="p-2 text-amber-600 hover:bg-amber-50 rounded"
                      >
                        <Trash2 size={16} />
                      </button>
                    ) : (
                      <button
                        onClick={() => changeStatus(u.id, 'active')}
                        title="Aktiver"
                        className="p-2 text-green-600 hover:bg-green-50 rounded"
                      >
                        <CheckCircle2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Ventende invitasjoner */}
          {pending.length > 0 && (
            <div className="mt-6 pt-6 border-t">
              <h3 className="font-semibold text-gray-900 mb-3">Ventende invitasjoner ({pending.length})</h3>
              <div className="space-y-2">
                {pending.map((p) => (
                  <div key={p.id} className="border border-yellow-200 bg-yellow-50 rounded p-3 flex items-center justify-between">
                    <div>
                      <strong className="text-sm">{p.email}</strong>
                      <span className="ml-2 text-xs text-gray-600">{p.role === 'sensor' ? 'Sensor' : 'Medlem'}</span>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Sendt {new Date(p.created_at).toLocaleDateString('nb-NO')} · Utløper {new Date(p.expires_at).toLocaleDateString('nb-NO')}
                      </div>
                    </div>
                    <span className="text-xs px-2 py-1 bg-yellow-200 text-yellow-900 rounded-full">Venter på aktivering</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
